// apps/codeframe/src/motion/resolveColorTokens.ts
import type { MotionTimeline, MotionChannel } from '../store/types';

const COLOR_PROPS = new Set(['color', 'backgroundColor']);

/** True if any color channel keyframe uses a CSS var() (theme token). */
export function hasVarColors(tl: MotionTimeline): boolean {
  return tl.tracks.some((tr) =>
    tr.layers.some((l) =>
      l.channels.some(
        (c) => COLOR_PROPS.has(c.prop) && c.keyframes.some((k) => typeof k.value === 'string' && k.value.includes('var(')),
      ),
    ),
  );
}

/**
 * Return a timeline with every color-channel keyframe value mapped through
 * `resolve` (only `var(...)` values are touched; concrete colors/numbers pass
 * through). Lets the sampler interpolate concrete rgba while authors store
 * theme tokens like `var(--color-accent)` that follow the light/dark theme.
 * Pure — `resolve` supplies the DOM-dependent lookup.
 */
export function resolveColorTokens(tl: MotionTimeline, resolve: (raw: string) => string): MotionTimeline {
  let any = false;
  const mapChannel = (c: MotionChannel): MotionChannel => {
    if (!COLOR_PROPS.has(c.prop)) return c;
    let changed = false;
    const keyframes = c.keyframes.map((k) => {
      if (typeof k.value === 'string' && k.value.includes('var(')) {
        const v = resolve(k.value);
        if (v !== k.value) {
          changed = true;
          return { ...k, value: v };
        }
      }
      return k;
    });
    if (!changed) return c;
    any = true;
    return { ...c, keyframes };
  };
  const tracks = tl.tracks.map((tr) => ({
    ...tr,
    layers: tr.layers.map((l) => ({ ...l, channels: l.channels.map(mapChannel) })),
  }));
  return any ? { ...tl, tracks } : tl; // unchanged → same ref (no per-tick churn)
}
